import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search } from "lucide-react";
import { buyAdminApi, BuyAdminManagedUser, BuyAdminSalesRep } from "../../../services/buyAdminApi";
import styles from "./MemberSalesRep.module.css";

type Assignment = { sales_rep_id: string | null; sales_rep_name: string | null };
type Props = {
  member: BuyAdminManagedUser;
  disabled?: boolean;
  onAssigned: (assignment: Assignment) => void;
};
const repName = (rep: BuyAdminSalesRep) =>
  [rep.sr_first_name, rep.sr_last_name].filter(Boolean).join(" ") || rep.sr_email || rep.sr_id;

export default function MemberSalesRep({ member, disabled, onAssigned }: Props) {
  const anchor = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const [reps, setReps] = useState<BuyAdminSalesRep[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const open = Boolean(position);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setError("");
    buyAdminApi.getBuyAdminSalesReps().then((response) => {
      if (active) setReps(response.data || []);
    }).catch((failure) => {
      if (active) setError(failure?.message || "Unable to load sales representatives. Close and reopen to retry.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: MouseEvent) => {
      if (!menu.current?.contains(event.target as Node) && !anchor.current?.contains(event.target as Node)) {
        setPosition(null);
      }
    };
    const close = () => setPosition(null);
    const onScroll = (event: Event) => {
      if (!menu.current?.contains(event.target as Node)) close();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") { close(); anchor.current?.focus(); }
    };
    document.addEventListener("mousedown", closeOutside);
    document.addEventListener("keydown", escape);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      document.removeEventListener("keydown", escape);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  const assign = async (id: string | null) => {
    if (saving || (member.sales_rep_id || null) === id) return;
    setSaving(true);
    setError("");
    try {
      const response = await buyAdminApi.assignBuyMemberSalesRep(member.id, id);
      onAssigned(response.data);
      setPosition(null);
      anchor.current?.focus();
    } catch (failure: any) {
      setError(failure?.message || "Unable to save sales representative. Please try again.");
    } finally { setSaving(false); }
  };

  const query = search.trim().toLowerCase();
  const filtered = reps.filter((rep) => `${repName(rep)} ${rep.sr_email || ""} ${rep.sr_id}`.toLowerCase().includes(query));
  const name = member.sales_rep_name || member.sales_rep_id || "None";

  return <>
    <button type="button" ref={anchor} className={styles.trigger}
      aria-label={`Sales rep for ${member.full_name || member.email}: ${name}`}
      aria-expanded={open} aria-haspopup="dialog" disabled={disabled || saving}
      title={name} onClick={() => {
        if (open) { setPosition(null); return; }
        const rect = anchor.current!.getBoundingClientRect();
        const width = Math.min(280, window.innerWidth - 16);
        const height = Math.min(320, window.innerHeight - 24);
        setPosition({ width, left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
          top: rect.bottom + height + 8 > window.innerHeight ? Math.max(8, rect.top - height - 6) : rect.bottom + 6 });
        setSearch("");
      }}>
      <span>{saving ? "Saving..." : name}</span><ChevronDown size={12} />
    </button>
    {position && createPortal(
      <div ref={menu} className={`${styles.menu} buy-admin-theme-scope`} style={position} role="dialog" aria-label="Assign sales representative" aria-busy={saving}>
        <div className={styles.search}><Search size={14} />
          <input autoFocus aria-label="Search sales reps" placeholder="Search sales reps..." value={search}
            onChange={(event) => setSearch(event.target.value)} />
        </div>
        {error && <div className={styles.error} role="alert">{error}</div>}
        <button type="button" className={`${styles.option} ${!member.sales_rep_id ? styles.selected : ""}`}
          disabled={saving} onClick={() => assign(null)}>None{!member.sales_rep_id && <Check size={14} />}</button>
        {loading ? <div className={styles.message}>Loading...</div> : filtered.map((rep) => (
          <button type="button" key={rep.sr_id} title={repName(rep)}
            className={`${styles.option} ${member.sales_rep_id === rep.sr_id ? styles.selected : ""}`}
            disabled={saving} onClick={() => assign(rep.sr_id)}>
            <span>{repName(rep)}</span>{member.sales_rep_id === rep.sr_id && <Check size={14} />}
          </button>
        ))}
        {!loading && !error && filtered.length === 0 && <div className={styles.message}>No sales reps</div>}
      </div>, document.body)}
  </>;
}
